import { CompareHelper, ConvertHelper, GUID } from '@barthauer/abc/core';
import { IPersistable, PersistenceService, PersistMode } from '@barthauer/abc/core/data';
import { ResourceSet } from '@barthauer/abc/core/resources';
import { MtResource, MtResourceEvent, MtResourceEventType, MtResourceType } from '../models/mtData.resources.model';
import { ResourceListService } from '../resources/resource-list/resource-list.service';
import {
  ResourceEventTypeKindDeviceOrMachine,
  ResourceEventTypeKindMaterial,
  ResourceEventTypeKindPersonnel,
  ResourceEventTypeKindVehicle,
  ResourcesConstants,
  ResourceTypeKind,
} from '../resources/resources.model';
import { AppService } from '../services/app.service';
import { ViewModelHelper } from '../shared/helpers';
import { ISelfDescribable } from './mobileData.viewModel';

export interface IMobileResourceType extends ISelfDescribable {
  name: string;
  selected: boolean;
  resources: MobileResourceViewModel[];
}

export class MobileResourceTypeViewModel implements IMobileResourceType {
  private _resources: MobileResourceViewModel[];
  private _resourceEventTypes: MobileResourceEventTypeViewModel[];
  private _resourceEventTypeGroups: MobileResourceEventTypeGroupViewModel[];
  private _selected: boolean;
  private persistenceService: PersistenceService;
  private resourceListService: ResourceListService;
  private mtResourceType: MtResourceType;

  get name(): string {
    return this.mtResourceType.Name;
  }

  get kind(): string {
    return this.mtResourceType.Kind;
  }

  get selected(): boolean {
    return this._selected;
  }
  set selected(value: boolean) {
    this._selected = value;
  }
  get defaultUnit(): string {
    return this.mtResourceType.DefaultUnit;
  }

  get resources(): MobileResourceViewModel[] {
    if (!this._resources) {
      this._resources = [];
      for (const res of this.mtResourceType.Resources) {
        const t: MobileResourceViewModel = new MobileResourceViewModel(this.resourceSet, this.orderId);
        t.load(this.persistenceService, res, this, this.resourceListService);
        this._resources.push(t);
      }
    }
    return this._resources;
  }
  get resourceEventTypes(): MobileResourceEventTypeViewModel[] {
    if (!this._resourceEventTypes) {
      this._resourceEventTypes = [];
      for (const res of this.mtResourceType.ResourceEventTypes) {
        const t: MobileResourceEventTypeViewModel = new MobileResourceEventTypeViewModel();
        t.load(res);
        this._resourceEventTypes.push(t);
      }
    }
    return this._resourceEventTypes;
  }

  get resourceEventTypeGroups(): MobileResourceEventTypeGroupViewModel[] {
    if (!this._resourceEventTypeGroups) {
      this._resourceEventTypeGroups = [];

      const groupedEvtTypes: Map<string, MobileResourceEventTypeViewModel[]> =
        ViewModelHelper.groupArray<MobileResourceEventTypeViewModel>(this.resourceEventTypes, 'kind');

      groupedEvtTypes.forEach((v, k) => {
        const group = new MobileResourceEventTypeGroupViewModel(this);
        group.resourceEventTypes = v;
        this._resourceEventTypeGroups.push(group);
      });
      this._resourceEventTypeGroups.sort((a, b) =>
        a.resourceEventTypeKind > b.resourceEventTypeKind
          ? 1
          : b.resourceEventTypeKind > a.resourceEventTypeKind
            ? -1
            : 0,
      );
    }
    return this._resourceEventTypeGroups;
  }

  get iconName(): string {
    switch (this.kind) {
      case ResourceTypeKind.Vehicle:
        return 'Resource-Type-Vehicle-Image';
      case ResourceTypeKind.DeviceOrMachine:
        return 'Resource-Type-Device-Machine-Image';
      case ResourceTypeKind.Personnel:
        return 'Resource-Type-Personnel-Image';
      case ResourceTypeKind.Material:
        return 'Resource-Type-Material-Image';
      default: //"Sonstige"
        return 'Resource-Type-Other-Image';
    }
  }

  constructor(
    private resourceSet: ResourceSet,
    private orderId: string,
  ) {}

  getValues(): string[] {
    throw new Error('Method not implemented.');
  }

  async load(
    persistenceService: PersistenceService,
    mtResourceType: MtResourceType,
    resourceListService: ResourceListService,
  ): Promise<void> {
    this.persistenceService = persistenceService;
    this.mtResourceType = mtResourceType;
    this.selected = true;
    this.resourceListService = resourceListService;
  }
}

export class MobileResourceViewModel {
  private _resourceEvents: MobileResourceEventViewModel[];
  private _resourceEventGroups: MobileResourceEventGroupViewModel[];
  private persistenceService: PersistenceService;
  private resourceListService: ResourceListService;
  private mtResource: MtResource;
  public resourceType: MobileResourceTypeViewModel;

  get id(): string {
    return this.mtResource.Id;
  }

  get name(): string {
    return this.mtResource.Name;
  }

  get login(): string {
    return this.mtResource.Login;
  }

  get defaultUnit(): string {
    return this.mtResource.DefaultUnit;
  }

  private get resourceEvents(): MobileResourceEventViewModel[] {
    if (!this._resourceEvents) {
      this._resourceEvents = [];
      for (const res of this.mtResource.ResourceEvents) {
        const t: MobileResourceEventViewModel = new MobileResourceEventViewModel();
        const evttypeGroup = this.findEventTypeGroup(res.IdRefResourceEventType);
        if (evttypeGroup) {
          t.load(this.persistenceService, res, this, evttypeGroup, this.resourceListService);
          this._resourceEvents.push(t);
        } else {
          console.log('EventTypeGroup not found. Weird situation. R-24-00274');
        }
      }
    }
    return this._resourceEvents;
  }

  private findEventTypeGroup(resourceEventTypeId: string): MobileResourceEventTypeGroupViewModel {
    const resEvtType: MobileResourceEventTypeViewModel = this.resourceType.resourceEventTypes.find(
      (et) => et.id === resourceEventTypeId,
    );
    if (resEvtType) {
      return this.resourceType.resourceEventTypeGroups.find((g) => g.resourceEventTypeKind === resEvtType.kind);
    } else {
      return undefined;
    }
  }

  get resourceEventGroups(): MobileResourceEventGroupViewModel[] {
    if (!this._resourceEventGroups) {
      this._resourceEventGroups = [];

      const groupedEvts: Map<string, MobileResourceEventViewModel[]> =
        ViewModelHelper.groupArray<MobileResourceEventViewModel>(this.resourceEvents, 'resourceEventTypeKind');

      //use EventTYPES to create the groups
      for (const resEvtTypeGroup of this.resourceType.resourceEventTypeGroups) {
        const group = new MobileResourceEventGroupViewModel(this, this.resourceSet);
        group.resourceEventTypeGroup = resEvtTypeGroup;
        if (groupedEvts.has(resEvtTypeGroup.resourceEventTypeKind)) {
          group.resourceEvents = groupedEvts.get(resEvtTypeGroup.resourceEventTypeKind);
          group.resourceEvents.sort((a, b) => (a.index > b.index ? -1 : b.index > a.index ? 1 : 0));
        }
        this._resourceEventGroups.push(group);
      }
    }
    return this._resourceEventGroups;
  }

  constructor(
    private resourceSet: ResourceSet,
    private orderId: string,
  ) {}

  public addNewResourceEvent(resourceEventTypeGroup: MobileResourceEventTypeGroupViewModel) {
    const resEvtGr: MobileResourceEventGroupViewModel = this.resourceEventGroups.find(
      (resEvtGr) => resEvtGr.resourceEventTypeGroup === resourceEventTypeGroup,
    );

    const newEvt: MobileResourceEventViewModel = new MobileResourceEventViewModel();
    const newMtResEvt: MtResourceEvent = newEvt.createNewWithDefaultValues(
      this.persistenceService,
      this,
      resEvtGr,
      this.orderId,
      this.resourceListService,
    );

    this.mtResource.ResourceEvents.push(newMtResEvt);
    this.resourceEvents.push(newEvt);
    this.resourceListService.insertResourceEvent(newEvt);
    //sorted by index; we assume that it has the right order
    resEvtGr.resourceEvents.unshift(newEvt);
  }

  public removeResourceEvent(
    resourceEventGroupViewModel: MobileResourceEventGroupViewModel,
    indexInGroup: number,
  ): void {
    const resEvent: MobileResourceEventViewModel = resourceEventGroupViewModel.resourceEvents[indexInGroup];
    if (resEvent.hasPersistedDataObject) {
      this.persistenceService.addObjectForDelete(resEvent);
      this.persistenceService.persistPendingChanges(ResourcesConstants.PersistenceCategory_ResourceEvent);
    }
    this.persistenceService.removeObject(resEvent);

    this.removeResourceEventsFromLists(resEvent);
    resourceEventGroupViewModel.resourceEvents.splice(indexInGroup, 1);
  }

  private removeResourceEventsFromLists(resEvent: MobileResourceEventViewModel): void {
    //remove from mt-data-structure
    const mtIndex: number = this.mtResource.ResourceEvents.findIndex((r) => r.Id === resEvent.id);
    this.mtResource.ResourceEvents.splice(mtIndex, 1);
    //remove from viewmodel-structure
    const vmIndex: number = this.resourceEvents.indexOf(resEvent);
    this.resourceEvents.splice(vmIndex, 1);
    //remove from resource-grid-structure
    this.resourceListService.deleteResourceEvent(resEvent);
  }

  async load(
    persistenceService: PersistenceService,
    mtResource: MtResource,
    resourceType: MobileResourceTypeViewModel,
    resourceListService: ResourceListService,
  ): Promise<void> {
    this.persistenceService = persistenceService;
    this.mtResource = mtResource;
    this.resourceType = resourceType;
    this.resourceListService = resourceListService;
  }
}

export class MobileResourceEventGroupViewModel {
  private resource: MobileResourceViewModel;
  public resourceEvents: MobileResourceEventViewModel[];
  public resourceEventTypeGroup: MobileResourceEventTypeGroupViewModel;

  get existsAnyResourceEventToday(): boolean {
    return this.resourceEvents.some((e) => ViewModelHelper.areDatesEqualIgnoreTime(e.date, new Date()));
  }

  get maxIndex(): number {
    return this.resourceEvents.reduce((oa, u) => Math.max(oa, u.index), 0);
  }

  get lastFilledEndValue(): number | undefined {
    //assume that this.resourceEvents is already desc sorted by index
    const lastResourceEventWithValue: MobileResourceEventViewModel = ViewModelHelper.firstOrDefault(
      this.resourceEvents,
      (item) => item.endValue !== undefined && item.endValue !== null,
    );
    return lastResourceEventWithValue?.endValue;
  }

  get summaryText(): string {
    const sum = this.resourceEvents
      .reduce((ty, u) => ty + (u.resultValue ?? 0), 0)
      .toLocaleString(undefined, {
        maximumFractionDigits: 1,
      });

    const unit =
      this.resourceEvents.find((evt) => evt.unit)?.unit ??
      this.resource.defaultUnit ??
      this.resourceEventTypeGroup.resourceEventTypes.find((evt) => evt.defaultUnit)?.defaultUnit ??
      this.resource.resourceType.defaultUnit ??
      '';

    switch (this.resource.resourceType.kind) {
      case ResourceTypeKind.Vehicle:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindVehicle.Mileage:
            return this.resourceSet.get('Resources-ResourceEventVehicleMileageSummary', sum, unit);
          case ResourceEventTypeKindVehicle.OperatingHours:
            return this.resourceSet.get('Resources-ResourceEventVehicleOperatingHoursSummary', sum, unit);
          case ResourceEventTypeKindVehicle.Downtime:
            return this.resourceSet.get('Resources-ResourceEventVehicleDowntimeSummary', sum, unit);
          case ResourceEventTypeKindVehicle.WaterFilling:
            return this.resourceSet.get('Resources-ResourceEventVehicleWaterFillingSummary', sum, unit);
        }
      case ResourceTypeKind.DeviceOrMachine:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindDeviceOrMachine.OperatingHours:
            return this.resourceSet.get('Resources-ResourceEventDeviceOrMachineOperatingHoursSummary', sum, unit);
        }
      case ResourceTypeKind.Personnel:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindPersonnel.WorkingTimes:
            return this.resourceSet.get('Resources-ResourceEventPersonnelWorkingTimesSummary', sum, unit);
        }
      case ResourceTypeKind.Material:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindMaterial.MaterialExpense:
            return this.resourceSet.get('Resources-ResourceEventMaterialMaterialExpenseSummary', sum, unit);
        }
      default: //sonstiges
        return this.resourceEventTypeGroup.resourceEventTypeKindName + ': ' + sum + (unit ? ' ' + unit : '');
    }
  }

  constructor(
    resource: MobileResourceViewModel,
    private resourceSet: ResourceSet,
  ) {
    this.resource = resource;
    this.resourceEvents = new Array<MobileResourceEventViewModel>();
  }

  public addNewResourceEvent(): void {
    this.resource.addNewResourceEvent(this.resourceEventTypeGroup);
  }
  public removeResourceEvent(index: number): void {
    this.resource.removeResourceEvent(this, index);
  }
}

export class MobileResourceEventViewModel implements IPersistable {
  private _dateCache: Date = null;
  private _endDateCache: Date = null;
  private _timeCache: Date = null;
  private _endTimeCache: Date = null;
  private persistenceService: PersistenceService;
  private resourceListService: ResourceListService;
  private mtResourceEvent: MtResourceEvent;
  private stopSyncing: boolean;
  public resource: MobileResourceViewModel;
  public resourceEventTypeGroup: MobileResourceEventTypeGroupViewModel;

  get id(): string {
    return this.mtResourceEvent.Id;
  }
  //filled after saved new object
  set id(value: string) {
    this.mtResourceEvent.Id = value;
  }

  get hasPersistedDataObject(): boolean {
    return !CompareHelper.isNullorEmpty(this.mtResourceEvent.Id);
  }

  get objectType(): string {
    return ResourcesConstants.ObjectTypeName_MtResourceEvent;
  }

  get persistenceCategory(): string {
    return ResourcesConstants.PersistenceCategory_ResourceEvent;
  }

  get index(): number {
    return this.mtResourceEvent.Index;
  }

  get name(): string {
    return this.mtResourceEvent.Name;
  }

  get code(): string {
    return this.mtResourceEvent.Code;
  }

  get resultValue(): number {
    return this.mtResourceEvent.ResultValue;
  }

  set resultValue(value: number) {
    if (this.mtResourceEvent.ResultValue !== value) {
      this.persistenceService.addObjectValue(this, 'ResultValue', value, this.mtResourceEvent.ResultValue);
      this.mtResourceEvent.ResultValue = value;
      this.syncValueFields('resultValue');
    }
  }

  get date(): Date {
    if (!this._dateCache) {
      if (this.mtResourceEvent.Date) {
        this._dateCache = new Date(this.mtResourceEvent.Date);
      }
    }
    return this._dateCache;
  }

  set date(value: Date) {
    const d: string = ConvertHelper.toISODateString(value);
    this._dateCache = null;
    if (this.mtResourceEvent.Date !== d) {
      this.persistenceService.addObjectValue(this, 'Date', d, this.mtResourceEvent.Date);
      this.mtResourceEvent.Date = d;
      this._timeCache = null;
      this.syncValueFields('date');
    }
  }

  get endDate(): Date {
    if (!this._endDateCache) {
      if (this.mtResourceEvent.EndDate) {
        this._endDateCache = new Date(this.mtResourceEvent.EndDate);
      }
    }
    return this._endDateCache;
  }

  set endDate(value: Date) {
    const d: string = ConvertHelper.toISODateString(value);
    this._endDateCache = null;
    if (this.mtResourceEvent.EndDate !== d) {
      this.persistenceService.addObjectValue(this, 'EndDate', d, this.mtResourceEvent.EndDate);
      this.mtResourceEvent.EndDate = d;
      this._endTimeCache = null;
      this.syncValueFields('endDate');
    }
  }

  get time(): Date {
    if (!this._timeCache) {
      if (this.mtResourceEvent.Time) {
        this._timeCache = ViewModelHelper.parseDateTime(this.date, this.mtResourceEvent.Time);
      }
    }
    return this._timeCache;
  }

  set time(value: Date) {
    const t: string = value ? ViewModelHelper.ensureValidIsoString(this.mtResourceEvent.Time, value) : null;
    this._timeCache = null;
    if (this.mtResourceEvent.Time !== t) {
      this.persistenceService.addObjectValue(this, 'Time', t, this.mtResourceEvent.Time);
      this.mtResourceEvent.Time = t;
      this.syncValueFields('time');
    }
  }

  get displayTime(): Date {
    return this.time ? this.time : this.date;
  }

  get endTime(): Date {
    if (!this._endTimeCache) {
      if (this.mtResourceEvent.EndTime) {
        this._endTimeCache = ViewModelHelper.parseDateTime(this.endDate, this.mtResourceEvent.EndTime);
      }
    }
    return this._endTimeCache;
  }

  set endTime(value: Date) {
    const t: string = value ? ViewModelHelper.ensureValidIsoString(this.mtResourceEvent.EndTime, value) : null;
    this._endTimeCache = null;
    if (this.mtResourceEvent.EndTime !== t) {
      this.persistenceService.addObjectValue(this, 'EndTime', t, this.mtResourceEvent.EndTime);
      this.mtResourceEvent.EndTime = t;
      this.syncValueFields('endTime');
    }
  }

  get displayEndTime(): Date {
    return this.endTime ? this.endTime : this.endDate;
  }

  get value(): number {
    return this.mtResourceEvent.Value;
  }

  set value(value: number) {
    if (this.mtResourceEvent.Value !== value) {
      this.persistenceService.addObjectValue(this, 'Value', value, this.mtResourceEvent.Value);
      this.mtResourceEvent.Value = value;
      this.syncValueFields('value');
    }
  }

  get endValue(): number {
    return this.mtResourceEvent.EndValue;
  }

  set endValue(value: number) {
    if (this.mtResourceEvent.EndValue !== value) {
      this.persistenceService.addObjectValue(this, 'EndValue', value, this.mtResourceEvent.EndValue);
      this.mtResourceEvent.EndValue = value;
      this.syncValueFields('endValue');
    }
  }

  private syncValueFields(nameOfChangedField: string): void {
    if (!this.stopSyncing) {
      this.stopSyncing = true;
      if (nameOfChangedField === 'value' || nameOfChangedField === 'endValue') {
        if (this.shouldSyncField('resultValue', nameOfChangedField)) {
          this.resultValue = this.endValue - this.value;
        }
      } else if (nameOfChangedField === 'resultValue') {
        if (this.shouldSyncField('endValue', nameOfChangedField)) {
          this.endValue = this.value + this.resultValue;
        }
        if (this.shouldSyncField('endDate', nameOfChangedField)) {
          const newEndDateTime: Date | undefined = this.calculateEndDateTime();
          if (newEndDateTime) {
            this.endDate = newEndDateTime;
            this._endDateCache = null;
          }
        }
        if (this.shouldSyncField('endTime', nameOfChangedField)) {
          const newEndDateTime: Date | undefined = this.calculateEndDateTime();
          if (newEndDateTime) {
            this.endTime = newEndDateTime;
            this._endTimeCache = null;
          }
        }
      } else if (
        nameOfChangedField === 'time' ||
        nameOfChangedField === 'endTime' ||
        nameOfChangedField === 'date' ||
        nameOfChangedField === 'endDate' ||
        nameOfChangedField === 'timespanOff'
      ) {
        if (this.shouldSyncField('resultValue', nameOfChangedField)) {
          const newResult: number | undefined = this.calculateResultValueTime();
          if (ViewModelHelper.isValidNumber(newResult)) {
            this.resultValue = newResult;
          }
        }
      } else if (nameOfChangedField === 'idRefResourceEventType') {
        this.unit = this.resourceEventType.defaultUnit;
      }
      this.stopSyncing = false;
    }
  }

  private shouldSyncField(fieldName: string, antagonistFieldName: string): boolean {
    switch (this.resourceEventTypeGroup.resourceTypeKind) {
      case ResourceTypeKind.Vehicle:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindVehicle.Mileage:
            if (antagonistFieldName === 'value' || antagonistFieldName === 'endValue') {
              return fieldName === 'resultValue';
            } else if (antagonistFieldName === 'resultValue') {
              return fieldName === 'endValue';
            }
            break;
          case ResourceEventTypeKindVehicle.OperatingHours:
          case ResourceEventTypeKindVehicle.Downtime:
            if (
              antagonistFieldName === 'time' ||
              antagonistFieldName === 'endTime' ||
              antagonistFieldName === 'date' ||
              antagonistFieldName === 'endDate' ||
              antagonistFieldName === 'timespanOff'
            ) {
              return fieldName === 'resultValue';
            } else if (antagonistFieldName === 'resultValue') {
              return fieldName === 'endDate' || fieldName === 'endTime';
            }
            break;
        }
        break;
      case ResourceTypeKind.Personnel:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindPersonnel.WorkingTimes:
            if (
              antagonistFieldName === 'time' ||
              antagonistFieldName === 'endTime' ||
              antagonistFieldName === 'date' ||
              antagonistFieldName === 'endDate' ||
              antagonistFieldName === 'timespanOff'
            ) {
              return fieldName === 'resultValue';
            } else if (antagonistFieldName === 'resultValue') {
              return fieldName === 'endDate' || fieldName === 'endTime';
            }
            break;
        }
        break;
      case ResourceTypeKind.DeviceOrMachine:
        switch (this.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindDeviceOrMachine.OperatingHours:
            if (
              antagonistFieldName === 'time' ||
              antagonistFieldName === 'endTime' ||
              antagonistFieldName === 'date' ||
              antagonistFieldName === 'endDate' ||
              antagonistFieldName === 'timespanOff'
            ) {
              return fieldName === 'resultValue';
            } else if (antagonistFieldName === 'resultValue') {
              return fieldName === 'endDate' || fieldName === 'endTime';
            }
            break;
        }
        break;
      default: {
        //"Sonstige"
        // if (
        //   antagonistFieldName === 'time' ||
        //   antagonistFieldName === 'endTime' ||
        //   antagonistFieldName === 'date' ||
        //   antagonistFieldName === 'endDate' ||
        //   antagonistFieldName === 'timespanOff'
        // ) {
        //   return fieldName === 'resultValue';
        // } else if (antagonistFieldName === 'resultValue') {
        //   return fieldName === 'endDate' || fieldName === 'endTime';
        // }
        // break;
        return false; //No needs to synchronize the values according to spec
      }
    }
    return false;
  }

  private calculateEndDateTime(): Date | undefined {
    let retDate: Date | undefined = undefined;
    if (this.time && ViewModelHelper.isValidNumber(this.resultValue)) {
      let timeInMilliseconds: number = this.resultValue;
      if (ViewModelHelper.isValidNumber(this.timespanOffAsHours)) {
        timeInMilliseconds += this.timespanOffAsHours;
      }
      timeInMilliseconds *= ViewModelHelper.HOUR_IN_MILLISECONDS;
      retDate = new Date(this.time.getTime() + timeInMilliseconds);
    }

    return retDate;
  }

  private calculateResultValueTime(): number | undefined {
    let retValue: number | undefined = undefined;
    if (this.time && this.endTime) {
      const diffInMilliseconds = this.endTime.getTime() - this.time.getTime();
      const diffInHours = diffInMilliseconds / ViewModelHelper.HOUR_IN_MILLISECONDS;

      if (ViewModelHelper.isValidNumber(this.timespanOffAsHours)) {
        retValue = diffInHours - this.timespanOffAsHours;
      } else {
        retValue = diffInHours;
      }
    }

    return retValue;
  }

  private get timespanOff(): number {
    return this.mtResourceEvent.TimespanOff ?? 0;
  }

  private set timespanOff(value: number) {
    if (this.mtResourceEvent.TimespanOff !== value) {
      this.persistenceService.addObjectValue(this, 'TimespanOff', value, this.mtResourceEvent.TimespanOff);
      this.mtResourceEvent.TimespanOff = value;
      this.syncValueFields('timespanOff');
    }
  }

  get timespanOffAsHours(): number {
    return this.timespanOff ? this.timespanOff * ViewModelHelper.DAY_IN_HOURS : 0;
  }

  set timespanOffAsHours(value: number) {
    this.timespanOff = value ? value / ViewModelHelper.DAY_IN_HOURS : 0;
  }

  get unit(): string {
    return (
      this.mtResourceEvent.Unit ??
      this.resource.defaultUnit ??
      this.resourceEventType.defaultUnit ??
      this.resource.resourceType.defaultUnit
    );
  }

  set unit(value: string) {
    if (this.mtResourceEvent.Unit !== value) {
      this.persistenceService.addObjectValue(this, 'Unit', value, this.mtResourceEvent.Unit);
      this.mtResourceEvent.Unit = value;
    }
  }

  //resourceEventTypeKind: used as string above - dont delete!
  get resourceEventTypeKind(): string {
    return this.resourceEventType?.kind;
  }

  get valueFormat(): string {
    return ResourcesConstants.ResultValueFormatString + (this.unit ? ' ' + this.unit : '');
  }

  get idRefResourceEventType(): string {
    return this.mtResourceEvent.IdRefResourceEventType;
  }

  set idRefResourceEventType(value: string) {
    if (this.mtResourceEvent.IdRefResourceEventType !== value) {
      this.persistenceService.addObjectValue(
        this,
        'IdRefResourceEventType',
        value,
        this.mtResourceEvent.IdRefResourceEventType,
      );
      this.mtResourceEvent.IdRefResourceEventType = value;
      this.syncValueFields('idRefResourceEventType');
    }
  }

  get comment(): string {
    return this.mtResourceEvent.Comment;
  }

  set comment(value: string) {
    if (this.mtResourceEvent.Comment !== value) {
      this.persistenceService.addObjectValue(
        this,
        "Comments[SeqNr=1 And Identifier='Standard'].Text",
        value,
        this.mtResourceEvent.Comment,
      );
      this.persistenceService.addObjectValue(
        this,
        "Comments[SeqNr=1 And Identifier='Standard'].Comment",
        value,
        this.mtResourceEvent.Comment,
      );
      this.mtResourceEvent.Comment = value;
    }
  }

  get resourceTypeSelected(): boolean {
    return this.resource.resourceType.selected;
  }
  get resourceName(): string {
    return this.resource.name;
  }

  get resourceType(): string {
    return this.resource.resourceType.name;
  }

  get resourceEventTypeKindName(): string {
    return this.resourceEventTypeGroup.resourceEventTypeKindName;
  }

  private get resourceEventType(): MobileResourceEventTypeViewModel {
    return this.resourceEventTypeGroup.resourceEventTypes.find((evtType) => evtType.id === this.idRefResourceEventType);
  }

  constructor() {}

  public async load(
    persistenceService: PersistenceService,
    mtResourceEvent: MtResourceEvent,
    resource: MobileResourceViewModel,
    resourceEventTypeGroup: MobileResourceEventTypeGroupViewModel,
    resourceListService: ResourceListService,
  ): Promise<void> {
    this.persistenceService = persistenceService;
    this.mtResourceEvent = mtResourceEvent;
    this.resource = resource;
    this.resourceEventTypeGroup = resourceEventTypeGroup;
    this.resourceListService = resourceListService;
  }

  public createNewWithDefaultValues(
    persistenceService: PersistenceService,
    parentResource: MobileResourceViewModel,
    resourceEventGroup: MobileResourceEventGroupViewModel,
    orderId: string,
    resourceListService: ResourceListService,
  ): MtResourceEvent {
    const defaultEventType: MobileResourceEventTypeViewModel =
      resourceEventGroup.resourceEventTypeGroup.resourceEventTypes[0];
    const workId: string = resourceEventGroup.resourceEventTypeGroup.workId;
    const now: Date = new Date();
    const newMtResEvt: MtResourceEvent = new MtResourceEvent({
      Id: GUID.newGUID(),
      Index: resourceEventGroup.maxIndex + 1,
      IdRefResourceEventType: defaultEventType.id,
      Date: ConvertHelper.toISODateString(now),
    });

    const lastResourceEvent: MobileResourceEventViewModel =
      resourceEventGroup.resourceEvents.length > 0 ? resourceEventGroup.resourceEvents[0] : undefined;

    switch (resourceEventGroup.resourceEventTypeGroup.resourceTypeKind) {
      case ResourceTypeKind.Vehicle:
        switch (resourceEventGroup.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindVehicle.Mileage:
            newMtResEvt.EndDate = ConvertHelper.toISODateString(now);
            newMtResEvt.Time = ConvertHelper.toISOTimeString(now);
            newMtResEvt.Value = resourceEventGroup.lastFilledEndValue;
            break;
          case ResourceEventTypeKindVehicle.OperatingHours:
            newMtResEvt.EndDate = ConvertHelper.toISODateString(now);
            newMtResEvt.Time = ConvertHelper.toISOTimeString(lastResourceEvent?.time);
            newMtResEvt.EndTime = ConvertHelper.toISOTimeString(lastResourceEvent?.endTime);
            newMtResEvt.TimespanOff = lastResourceEvent?.timespanOff;
            newMtResEvt.ResultValue = lastResourceEvent?.resultValue;
            break;
          case ResourceEventTypeKindVehicle.WaterFilling:
            newMtResEvt.Time = ConvertHelper.toISOTimeString(now);
            break;
          case ResourceEventTypeKindVehicle.Downtime:
            newMtResEvt.EndDate = ConvertHelper.toISODateString(now);
            newMtResEvt.Time = ConvertHelper.toISOTimeString(now);
            break;
        }
        break;
      case ResourceTypeKind.Personnel:
        switch (resourceEventGroup.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindPersonnel.WorkingTimes:
            newMtResEvt.EndDate = ConvertHelper.toISODateString(now);
            newMtResEvt.Time = ConvertHelper.toISOTimeString(lastResourceEvent?.time);
            newMtResEvt.EndTime = ConvertHelper.toISOTimeString(lastResourceEvent?.endTime);
            newMtResEvt.TimespanOff = lastResourceEvent?.timespanOff;
            newMtResEvt.ResultValue = lastResourceEvent?.resultValue;
            break;
        }
        break;
      case ResourceTypeKind.Material:
        switch (resourceEventGroup.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindMaterial.MaterialExpense:
            newMtResEvt.Time = ConvertHelper.toISOTimeString(now);
            break;
        }
        break;
      case ResourceTypeKind.DeviceOrMachine:
        switch (resourceEventGroup.resourceEventTypeGroup.resourceEventTypeKind) {
          case ResourceEventTypeKindDeviceOrMachine.OperatingHours:
            newMtResEvt.EndDate = ConvertHelper.toISODateString(now);
            newMtResEvt.Time = ConvertHelper.toISOTimeString(lastResourceEvent?.time);
            newMtResEvt.EndTime = ConvertHelper.toISOTimeString(lastResourceEvent?.endTime);
            newMtResEvt.TimespanOff = lastResourceEvent?.timespanOff;
            newMtResEvt.ResultValue = lastResourceEvent?.resultValue;
            break;
        }
        break;
      default: //"Sonstige"
        newMtResEvt.Time = ConvertHelper.toISOTimeString(now);
        break;
    }

    this.load(
      persistenceService,
      newMtResEvt,
      parentResource,
      resourceEventGroup.resourceEventTypeGroup,
      resourceListService,
    );

    this.persistenceService.addObjectForMerge(this);
    this.persistenceService.addObjectValue(this, 'ParentId', parentResource.id);
    this.persistenceService.addObjectValue(this, 'IdRefAuftrag', orderId);
    this.persistenceService.addObjectValue(this, 'Index', newMtResEvt.Index);
    this.persistenceService.addObjectValue(this, 'Name', defaultEventType.name);
    this.persistenceService.addObjectValue(this, 'Kind', defaultEventType.kind);
    this.persistenceService.addObjectValue(this, 'Code', defaultEventType.code);
    this.persistenceService.addObjectValue(this, 'Unit', defaultEventType.defaultUnit);
    this.persistenceService.addObjectValue(this, 'IdRefResourceEventType', newMtResEvt.IdRefResourceEventType);
    this.persistenceService.addObjectValue(this, 'Date', newMtResEvt.Date);
    this.persistenceService.addObjectValue(this, 'Time', newMtResEvt.Time);
    this.persistenceService.addObjectValue(this, 'TimespanOff', newMtResEvt.TimespanOff);
    if (newMtResEvt.EndDate) {
      this.persistenceService.addObjectValue(this, 'EndDate', newMtResEvt.EndDate);
    }
    if (newMtResEvt.EndTime) {
      this.persistenceService.addObjectValue(this, 'EndTime', newMtResEvt.EndTime);
    }
    if (workId) {
      this.persistenceService.addObjectValue(this, 'IdRefWork', workId);
    }

    return newMtResEvt;
  }

  revertChanges() {
    const pack = this.persistenceService.getPendingObjectPackage(this);
    if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
      pack.setOriginalFieldHistoryValue('ResultValue', (value) => (this.mtResourceEvent.ResultValue = value));
      pack.setOriginalFieldHistoryValue('Date', (value) => (this.mtResourceEvent.Date = value));
      pack.setOriginalFieldHistoryValue('EndDate', (value) => (this.mtResourceEvent.EndDate = value));
      pack.setOriginalFieldHistoryValue('Time', (value) => (this.mtResourceEvent.Time = value));
      pack.setOriginalFieldHistoryValue('EndTime', (value) => (this.mtResourceEvent.EndTime = value));
      pack.setOriginalFieldHistoryValue('Value', (value) => (this.mtResourceEvent.Value = value));
      pack.setOriginalFieldHistoryValue('EndValue', (value) => (this.mtResourceEvent.EndValue = value));
      pack.setOriginalFieldHistoryValue('TimespanOff', (value) => (this.mtResourceEvent.TimespanOff = value));
      pack.setOriginalFieldHistoryValue('Unit', (value) => (this.mtResourceEvent.Unit = value));
      pack.setOriginalFieldHistoryValue(
        'IdRefResourceEventType',
        (value) => (this.mtResourceEvent.IdRefResourceEventType = value),
      );
      pack.setOriginalFieldHistoryValue(
        "Comments[SeqNr=1 And Identifier='Standard'].Text",
        (value) => (this.mtResourceEvent.Comment = value),
      );
    }
    this._dateCache = null;
    this._endDateCache = null;
    this._timeCache = null;
    this._endTimeCache = null;
    this.resourceListService.updateResourceEvent(this);
  }

  changesPersisted(persistMode: PersistMode) {
    if (persistMode === PersistMode.Update || persistMode === PersistMode.Merge) {
      this.resourceListService.updateResourceEvent(this);
    }
  }
}

export class MobileResourceEventTypeGroupViewModel {
  private resourceType: MobileResourceTypeViewModel;
  public resourceEventTypes: MobileResourceEventTypeViewModel[];
  public workId: string;

  get resourceTypeKind(): string {
    return this.resourceType.kind;
  }

  get resourceEventTypeKind(): string {
    return this.resourceEventTypes[0].kind;
  }

  get resourceEventTypeKindName(): string {
    return this.resourceEventTypes[0].kindName;
  }

  get iconName(): string {
    switch (this.resourceTypeKind) {
      case ResourceTypeKind.Vehicle:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindVehicle.Mileage:
            return 'Resource-Event-Type-Vehicle-Mileage-Image';
          case ResourceEventTypeKindVehicle.OperatingHours:
            return 'Resource-Event-Type-Vehicle-Operating-Hours-Image';
          case ResourceEventTypeKindVehicle.Downtime:
            return 'Resource-Event-Type-Vehicle-Downtime-Image';
          case ResourceEventTypeKindVehicle.WaterFilling:
            return 'Resource-Event-Type-Vehicle-Water-Filling-Image';
        }
      case ResourceTypeKind.DeviceOrMachine:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindDeviceOrMachine.OperatingHours:
            return 'Resource-Event-Type-Device-Machine-Operating-Hours-Image';
        }
      case ResourceTypeKind.Personnel:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindPersonnel.WorkingTimes:
            return 'Resource-Event-Type-Personnel-Working-Times-Image';
        }
      case ResourceTypeKind.Material:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindMaterial.MaterialExpense:
            return 'Resource-Event-Type-Material-Material-Expense-Image';
        }
      default: //"Sonstige"
        return 'Resource-Event-Type-Other-Other-Image';
    }
  }

  get detailComponentRoute(): string {
    let componentName: string;
    switch (this.resourceTypeKind) {
      case ResourceTypeKind.Vehicle:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindVehicle.Mileage:
            componentName = AppService.Route_ResourceEventVehicleMileage;
            break;
          case ResourceEventTypeKindVehicle.OperatingHours:
            componentName = AppService.Route_ResourceEventVehicleOperatingHours;
            break;
          case ResourceEventTypeKindVehicle.Downtime:
            componentName = AppService.Route_ResourceEventVehicleDowntime;
            break;
          case ResourceEventTypeKindVehicle.WaterFilling:
            componentName = AppService.Route_ResourceEventVehicleWaterFilling;
            break;
        }
        break;
      case ResourceTypeKind.DeviceOrMachine:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindDeviceOrMachine.OperatingHours:
            componentName = AppService.Route_ResourceEventVehicleOperatingHours;
            break;
        }
        break;
      case ResourceTypeKind.Personnel:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindPersonnel.WorkingTimes:
            componentName = AppService.Route_ResourceEventPersonnelWorkingTimes;
            break;
        }
        break;
      case ResourceTypeKind.Material:
        switch (this.resourceEventTypeKind) {
          case ResourceEventTypeKindMaterial.MaterialExpense:
            componentName = AppService.Route_ResourceEventMaterialMaterialExpense;
            break;
        }
        break;
      default: {
        //"Sonstige"
        componentName = AppService.Route_ResourceEventOtherOther;
        break;
      }
    }
    if (this.workId) {
      componentName = AppService.Route_WorkDetail + '/' + componentName;
    }
    return componentName;
  }

  constructor(resourceType: MobileResourceTypeViewModel) {
    this.resourceType = resourceType;
    this.resourceEventTypes = new Array<MobileResourceEventTypeViewModel>();
  }
}

export class MobileResourceEventTypeViewModel {
  private mtResourceEventType: MtResourceEventType;

  get id(): string {
    return this.mtResourceEventType.Id;
  }

  get kind(): string {
    return this.mtResourceEventType.Kind;
  }
  get kindName(): string {
    return this.mtResourceEventType.KindName;
  }

  get name(): string {
    return this.mtResourceEventType.Name;
  }
  get code(): string {
    return this.mtResourceEventType.Code;
  }

  get defaultUnit(): string {
    return this.mtResourceEventType.DefaultUnit;
  }

  constructor() {}

  async load(mtResourceEventType: MtResourceEventType): Promise<void> {
    this.mtResourceEventType = mtResourceEventType;
  }
}
